import React, { useState } from 'react';

import Button from '../../../components/ui/Button';

const SearchAndFilter = ({ onFilter, onSort, availableYears = [] }) => {
  const [selectedYear, setSelectedYear] = useState('all');
  const [selectedColorScheme, setSelectedColorScheme] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [showFilters, setShowFilters] = useState(false);

  const colorSchemes = [
    { value: 'all', label: 'All Colors' },
    { value: 'monochrome', label: 'Monochrome' },
    { value: 'blue', label: 'Blue' },
    { value: 'green', label: 'Green' },
    { value: 'purple', label: 'Purple' },
    { value: 'pink', label: 'Pink' },
    { value: 'orange', label: 'Orange' }
  ];

  const sortOptions = [
    { value: 'newest', label: 'Most Recent', icon: 'ArrowDown' },
    { value: 'oldest', label: 'Oldest First', icon: 'ArrowUp' },
    { value: 'name', label: 'Name (A-Z)', icon: 'SortAsc' }
  ];

  const handleYearChange = (year) => {
    setSelectedYear(year);
    onFilter({ year: year === 'all' ? null : year, colorScheme: selectedColorScheme });
  };

  const handleColorSchemeChange = (scheme) => {
    setSelectedColorScheme(scheme);
    onFilter({ year: selectedYear === 'all' ? null : selectedYear, colorScheme: scheme });
  };

  const handleSortChange = (sort) => {
    setSortBy(sort);
    onSort(sort);
  };

  const handleClearFilters = () => {
    setSelectedYear('all');
    setSelectedColorScheme('all');
    setSortBy('newest');
    onFilter({ year: null, colorScheme: 'all' });
    onSort('newest');
  };

  const hasActiveFilters = selectedYear !== 'all' || selectedColorScheme !== 'all' || sortBy !== 'newest';

  return (
    <div className="space-y-4">
      {/* Filter Toggle Button (Mobile) */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowFilters(!showFilters)}
          iconName="Filter"
          iconPosition="left"
          iconSize={16}
          className="md:hidden"
        >
          Filters {hasActiveFilters && '(Active)'}
        </Button>

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearFilters}
            iconName="X"
            iconPosition="left"
            iconSize={14}
          >
            Clear Filters
          </Button>
        )}
      </div>
      {/* Filters Container */}
      <div className={`
        ${showFilters ? 'block' : 'hidden'} md:block
        bg-card border border-border rounded-lg p-4
      `}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Year Filter */}
          <div>
            <label className="font-caption text-xs text-muted-foreground mb-2 block">
              Year
            </label>
            <select
              value={selectedYear}
              onChange={(e) => handleYearChange(e?.target?.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm font-body text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">All Years</option>
              {availableYears?.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          {/* Color Scheme Filter */}
          <div>
            <label className="font-caption text-xs text-muted-foreground mb-2 block">
              Color Scheme
            </label>
            <select
              value={selectedColorScheme}
              onChange={(e) => handleColorSchemeChange(e?.target?.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm font-body text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {colorSchemes?.map(scheme => (
                <option key={scheme?.value} value={scheme?.value}>{scheme?.label}</option>
              ))}
            </select>
          </div>

          {/* Sort By */}
          <div>
            <label className="font-caption text-xs text-muted-foreground mb-2 block">
              Sort By
            </label>
            <select
              value={sortBy}
              onChange={(e) => handleSortChange(e?.target?.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm font-body text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {sortOptions?.map(option => (
                <option key={option?.value} value={option?.value}>
                  {option?.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Active Filters Summary */}
        {hasActiveFilters && (
          <div className="mt-3 pt-3 border-t border-border">
            <div className="flex items-center flex-wrap gap-2">
              <span className="font-caption text-xs text-muted-foreground">Active filters:</span>
              {selectedYear !== 'all' && (
                <span className="px-2 py-1 bg-primary/10 text-primary rounded text-xs font-caption">
                  Year: {selectedYear}
                </span>
              )}
              {selectedColorScheme !== 'all' && (
                <span className="px-2 py-1 bg-primary/10 text-primary rounded text-xs font-caption capitalize">
                  {selectedColorScheme}
                </span>
              )}
              {sortBy !== 'newest' && (
                <span className="px-2 py-1 bg-primary/10 text-primary rounded text-xs font-caption">
                  {sortOptions?.find(o => o?.value === sortBy)?.label}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchAndFilter;